import React from 'react';
import {
    billingModeLabel,
    documentBillingMode,
    type BillingModeStatement,
} from '../../services/billingMode';

/**
 * Die Abrechnungsart EINES Belegs als Chip — „kumulativ" oder „fortschreibend".
 *
 * WARUM als eigene Komponente: die Angabe steht an zwei Orten (Belegliste und
 * Belegkopf im Editor), und beide muessen dieselbe Ableitung benutzen. Sie
 * selbst zu bauen ist die Falle: das Modell steht in `details.billingMode`,
 * dort aber NUR bei kumulativen Belegen. Fortschreibend ist das FEHLEN des
 * Feldes — und mehrere Belegarten setzen es grundsaetzlich nie (Korrektur,
 * kaufmaennische Gutschrift, Gutschriftanzeige, Abschlag; ein Storno nur, wenn
 * es der Ursprungsbeleg trug). Wer dort das fehlende Feld als
 * „fortschreibend" liest, schreibt einer Gutschrift eine Abrechnungsart zu,
 * die sie nicht hat. Deshalb ausschliesslich ueber `documentBillingMode`, das
 * `null` fuer „keine Aussage" liefert.
 *
 * Bewusst ohne eigenes Farbsystem: kumulativ traegt das Emerald, das im
 * Anlagedialog schon den gesetzten Haken „Kumulativ abrechnen" markiert,
 * fortschreibend das neutrale Slate der uebrigen Belegchips. Amber bleibt den
 * Warnungen vorbehalten — keine der beiden Abrechnungsarten ist ein Fehler.
 */

/** Erklaerung im Tooltip — wortgleich zu den Erklaertexten des Anlagedialogs. */
const TITLES: Record<'CUMULATIVE' | 'PROGRESSIVE', string> = {
    CUMULATIVE:
        'Abrechnungsart: kumulativ — die Positionen zeigen den gesamten Leistungsstand bis zum Ende des Leistungszeitraums, und abgezogen wird, was die vorherigen Rechnungen dieses Projekts bereits gefordert haben.',
    PROGRESSIVE:
        'Abrechnungsart: fortschreibend — die Rechnung enthält nur die neuen Mengen des Leistungszeitraums, ohne Abzug der Vorrechnungen.',
};

const NO_STATEMENT_TITLE =
    'Diese Belegart trifft keine Aussage über die Abrechnungsart — Abschlag, Storno, Korrektur und Gutschriften rechnen weder kumulativ noch fortschreibend.';

export const BillingModeChip: React.FC<{
    /** Der Beleg selbst; gelesen werden nur Belegart und `details.billingMode`. */
    invoice: BillingModeStatement;
    /**
     * `withLabel` stellt „Abrechnungsart:" voran — fuer den Belegkopf, wo der
     * Chip allein zwischen Statusangaben steht und das Wort sonst raten liesse.
     * In der Liste bleibt er kurz, dort erklaert der Tooltip.
     */
    withLabel?: boolean;
    /**
     * Belege ohne Aussage mit „entfällt" zeigen statt gar nichts. Im Belegkopf
     * ja (die Angabe steht dann immer an derselben Stelle und sagt selbst, dass
     * sie nicht gilt), in der Liste nein (eine Spalte voller „entfällt" waere
     * Rauschen ueber zweihundert Zeilen).
     */
    showWhenUnstated?: boolean;
    className?: string;
}> = ({ invoice, withLabel = false, showWhenUnstated = false, className = '' }) => {
    const mode = documentBillingMode(invoice);
    if (mode == null && !showWhenUnstated) return null;

    const base =
        'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold';
    const tone =
        mode === 'CUMULATIVE'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : 'border-slate-200 bg-white text-slate-500';

    return (
        <span
            className={`${base} ${tone} ${className}`.trim()}
            title={mode ? TITLES[mode] : NO_STATEMENT_TITLE}
        >
            {withLabel && <span className="mr-1 font-medium text-slate-400">Abrechnungsart:</span>}
            {mode ? billingModeLabel(mode) : 'entfällt'}
        </span>
    );
};
